import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Package as PackageIcon, Sparkles, Award } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/components/Seo";

// Owner-approved main-tier order (customer-facing). Custom appears last.
const TIER_ORDER = ["silver", "gold", "platinum", "premium", "custom"];

// Themed visual cards — pure CSS gradients (no AI / stock food photos).
const TIER_THEME = {
  silver: {
    icon: PackageIcon,
    badge: "Best Value",
    // silver ribbon: cool grey → light steel
    band: "bg-gradient-to-br from-[#c9ccd1] via-[#e8ebef] to-[#a5a9b0]",
    text: "text-[#2a2b2d]",
    ring: "ring-[#c9ccd1]",
    cardBorder: "border-[#c9ccd1]",
  },
  gold: {
    icon: Award,
    badge: "Most Popular",
    // gold ribbon: saffron gold → warm amber
    band: "bg-gradient-to-br from-[#d4af37] via-[#f1cf6b] to-[#a37e13]",
    text: "text-[#2a2b2d]",
    ring: "ring-[#d4af37]",
    cardBorder: "border-[#d4af37]",
  },
  platinum: {
    icon: Crown,
    badge: "Platinum",
    // platinum ribbon: white-steel → deep charcoal with a rich burgundy edge
    band: "bg-gradient-to-br from-[#e7eaef] via-[#8f9095] to-[#1a1a1a]",
    text: "text-white",
    ring: "ring-[#7B1113]",
    cardBorder: "border-[#c8cdd6]",
  },
  premium: {
    icon: Crown,
    badge: "Legacy tier",
    band: "bg-gradient-to-br from-[#7B1113] via-[#3a0708] to-[#1a1a1a]",
    text: "text-white",
    ring: "ring-[#7B1113]",
    cardBorder: "border-[#7B1113]",
  },
  custom: {
    icon: Sparkles,
    badge: "Custom",
    band: "bg-gradient-to-br from-[#FDFBF7] via-[#f3ecdd] to-[#d4af37]",
    text: "text-[#2a2b2d]",
    ring: "ring-[#d4af37]",
    cardBorder: "border-[#7B1113]",
  },
};

export default function Packages() {
  useSeo({
    title: "Catering Packages in Mumbai — Silver, Gold & Premium | Tezz Khana",
    description: "Compare Tezz Khana catering packages for events in Mumbai — Silver, Gold, Premium and fully custom menus with per-guest pricing for weddings and celebrations.",
    path: "/packages",
  });
  const { settings, setSelectedPackageId } = useApp();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const currency = settings?.currency_symbol || "₹";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packages");
        // Sort so tiers appear in owner order
        const sorted = [...(data || [])].sort((a, b) => {
          const ai = TIER_ORDER.indexOf(a.tier); const bi = TIER_ORDER.indexOf(b.tier);
          return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
        });
        setPackages(sorted);
      } catch (e) { console.warn(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Hide any lingering "premium"-tier record if a "platinum" one now exists (rename in progress)
  const visible = packages.filter((p) => {
    if (p.tier === "premium" && packages.some((q) => q.tier === "platinum")) return false;
    return true;
  });

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">Curated Packages</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">Catering Packages</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Silver, Gold and Platinum builds — priced per person for events across Mumbai. Government taxes are charged separately as per the owner-approved Terms &amp; Conditions.
        </p>
      </div>

      {loading ? (
        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {[0,1,2].map((i) => (<div key={i} className="h-96 animate-pulse rounded-3xl border border-border/70 bg-muted/50" />))}
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="packages-empty-state" className="mt-12 rounded-2xl border border-dashed border-border/70 bg-card p-12 text-center">
          <p className="text-sm text-muted-foreground">Packages are being finalised. Please Request a Custom Quote and our team will craft one for you.</p>
          <Link data-testid="packages-empty-cta" to="/quote" className="btn-primary mt-6">Request Custom Quote</Link>
        </div>
      ) : (
        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {visible.map((p) => {
            const theme = TIER_THEME[p.tier] || TIER_THEME.custom;
            const Icon = theme.icon;
            const priceKnown = p.price !== null && p.price !== undefined;
            return (
              <article
                key={p.id}
                data-testid={`package-card-${p.tier}`}
                className={`group flex flex-col overflow-hidden rounded-3xl border-2 ${theme.cardBorder} bg-card shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10`}
              >
                {/* Themed visual band (no fake food photo) */}
                <div className={`relative aspect-[16/9] w-full overflow-hidden ${theme.band}`} data-testid={`package-visual-${p.tier}`}>
                  {/* grain */}
                  <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
                       style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>\")" }} />
                  {/* badge */}
                  <div className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-foreground">
                    {theme.badge}
                  </div>
                  {/* centre medallion */}
                  <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 ${theme.text}`}>
                    <span className={`grid h-16 w-16 place-items-center rounded-full bg-white/15 ring-2 ring-inset ${theme.ring} backdrop-blur-sm`}>
                      <Icon className="h-8 w-8" aria-hidden />
                    </span>
                    <div className="tk-wordmark text-lg uppercase tracking-[0.22em]" data-testid={`package-visual-name-${p.tier}`}>
                      {p.name || `${p.tier} Package`}
                    </div>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <div className="font-serif-display text-2xl font-semibold uppercase tracking-[0.06em] text-secondary" data-testid={`package-name-${p.tier}`}>
                    {p.name}
                  </div>
                  {p.description && <p className="mt-3 text-sm text-muted-foreground">{p.description}</p>}

                  <div className="mt-5 flex-1 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-secondary/80">
                      <Check className="h-4 w-4 text-primary" />
                      Minimum guests: {p.minimum_guests ?? <span className="text-primary font-semibold">TO BE CONFIGURED</span>}
                    </div>
                    <div className="flex items-center gap-2 text-secondary/80">
                      <Check className="h-4 w-4 text-primary" />
                      Category selection rules: {p.category_rules?.length ? `${p.category_rules.length} configured` : <span className="text-primary font-semibold">TO BE CONFIGURED</span>}
                    </div>
                  </div>

                  <div className="mt-6 border-t border-border/70 pt-4">
                    {priceKnown ? (
                      <div className="flex items-baseline gap-1" data-testid={`package-price-${p.tier}`}>
                        <span className="tk-wordmark text-3xl text-secondary">
                          {currency}{Number(p.price).toLocaleString("en-IN")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {p.pricing_unit ? ` / ${p.pricing_unit}` : ""}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm font-semibold text-primary" data-testid={`package-price-${p.tier}`}>Price on request</span>
                    )}
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent" data-testid={`package-taxes-${p.tier}`}>+ applicable taxes</div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to="/build-your-menu"
                        data-testid={`package-select-${p.tier}`}
                        onClick={() => setSelectedPackageId(p.id)}
                        className="btn-primary !py-2 !text-xs"
                      >
                        Select this package
                      </Link>
                      <Link to="/quote" data-testid={`package-request-${p.tier}`} className="btn-ghost !py-2 !text-xs">
                        Request Quote
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <p className="mt-8 max-w-3xl text-xs text-muted-foreground">
        Applicable government taxes are charged separately where required (per Terms &amp; Conditions clause 6).
        Decoration, tables, chairs, crockery, cutlery, live counters, transportation and special equipment are charged
        separately unless specifically included in the final written quotation.
      </p>
    </div>
  );
}
